interface ParallaxOptions {
	speed?: number;
	offset?: number;
	adjustOffset?: number;
	onlyAfterTop?: boolean;
	onlyBeforeTop?: boolean;
	reverse?: boolean;
	effects?: ('vertical' | 'horizontal' | 'fade' | 'verticalReverse' | 'horizontalReverse')[];
	parentLevel?: number;
}

export default class Parallax {

	// Static properties and methods
	private static readonly viewport = window;
	private static readonly document = document.documentElement;
	private static parallaxes: Parallax[] = [];
	private static isDesktop: boolean;
	private static hasInit: boolean = false;
	private static isCalculating: boolean = false;

	public static init(): void {
		const elements = document.querySelectorAll('[parallax]');
		if (!elements.length) {
			this.document.style.scrollBehavior = 'smooth';
			return;
		}
		elements.forEach((element) => {
			const parallax = new Parallax(element as HTMLElement);
			if (parallax.speed !== undefined) Parallax.parallaxes.push(parallax);
		});
		if (this.hasInit) return;
		this.hasInit = true;
		this.resize(() => {
			this.scroll();
			this.viewport.addEventListener('resize', () => this.resize());
			this.viewport.addEventListener('scroll', () => this.scroll());
		});
	}
	public static resize(callback?: () => void): void {
		if (this.isCalculating) return;
		this.isCalculating = true;
		this.document.style.scrollBehavior = 'auto';
		this.isDesktop = window.innerWidth > 1000;
		const origScroll = window.scrollY;
		this.viewport.addEventListener('scroll', () => {
			for (const parallax of this.parallaxes) parallax.resize();
			window.scrollTo({left: 0, top: origScroll, behavior: 'auto'});
			this.document.style.scrollBehavior = 'smooth';
			this.isCalculating = false;
			if (callback) callback();
		}, {once: true});
		window.scrollTo({left: 0, top: 0, behavior: 'auto'});
	}
	public static scroll(): void {
		if (!this.isDesktop || this.isCalculating) return;
		for (const parallax of this.parallaxes) parallax.scroll();
	}


	// Normal properties and methods
	public readonly speed: number;
	private readonly onlyAfterTop: boolean;
	private readonly onlyBeforeTop: boolean;
	private readonly reverse: boolean;
	private readonly adjustOffset: number;
	private readonly effects: {
		vertical: boolean;
		horizontal: boolean;
		fade: boolean;
		verticalReverse: boolean;
		horizontalReverse: boolean;
	};
	private offset!: number;
	private elementHeight!: number;
	private containerHeight!: number;
	private _offset?: number;

	constructor(private readonly element: HTMLElement) {
		const optionsAttr = this.element.getAttribute('parallax');
		const options: ParallaxOptions = optionsAttr && JSON.parse(optionsAttr);
		this.onlyAfterTop = !!options?.onlyAfterTop;
		this.onlyBeforeTop = !!options?.onlyBeforeTop;
		this.reverse = !!options?.reverse;
		this.effects = {
			vertical: options?.effects ? options.effects.indexOf('vertical') >= 0 : true,
			horizontal: options?.effects ? options.effects.indexOf('horizontal') >= 0 : false,
			fade: options?.effects ? options.effects.indexOf('fade') >= 0 : false,
			verticalReverse: options?.effects ? options.effects.indexOf('verticalReverse') >= 0 : true,
			horizontalReverse: options?.effects ? options.effects.indexOf('horizontalReverse') >= 0 : false
		};
		this.speed = options?.speed || 0;
		this.adjustOffset = options?.adjustOffset || 0;
		this._offset = options?.offset;
	}
	private resize(): void {
		if (Parallax.isDesktop) {
			const {top, height} = this.element.getBoundingClientRect();
			this.containerHeight = window.innerHeight / 2;
			this.elementHeight = height;
			this.offset = (this._offset === undefined ? top + this.position - this.containerHeight : this._offset) + this.adjustOffset;
		} else {
			this.element.style.transform = '';
			this.element.style.opacity = '1';
		}
	}
	private scroll(): void {
		if (!Parallax.isDesktop) return;

		const offsetAdjustment = this.reverse ? this.elementHeight : 0;
		const relativePosition = (this.offset - this.position + offsetAdjustment);
		const speedPosition = relativePosition * this.speed;

		if (
			(!this.effects.vertical && !this.effects.horizontal && !this.effects.verticalReverse && !this.effects.horizontalReverse) ||
			(this.onlyAfterTop && this.position < this.offset) ||
			(this.onlyBeforeTop && this.position > this.offset) ||
			(
				this.reverse && (
					(this.onlyAfterTop && this.position > this.offset + this.elementHeight) ||
					(this.onlyBeforeTop && this.position < this.offset + this.elementHeight)
				)
			)
		) {
			this.element.style.transform = '';
		} else {
			const x = this.effects.horizontal ? speedPosition : (this.effects.horizontalReverse ? -speedPosition : 0);
			const y = this.effects.vertical ? speedPosition : (this.effects.verticalReverse ? -speedPosition : 0);
			this.element.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
		}

		const opacity = ((): number | undefined => {
			const fadePosition = Math.abs(relativePosition);
			if (!this.effects.fade) return;
			if (this.reverse) {
				if ((this.onlyAfterTop && relativePosition < 0) || (this.onlyBeforeTop && relativePosition > 0)) return 1;
				if (fadePosition > this.containerHeight) return 0;
				return 1 - fadePosition / this.elementHeight;
			}
			if ((this.onlyAfterTop && relativePosition > 0) || (this.onlyBeforeTop && relativePosition < 0)) return;
			if (fadePosition > this.containerHeight) return 0;
			return 1 - fadePosition / this.containerHeight;
		})();
		this.element.style.opacity = opacity === undefined ? '' : opacity.toString();
	}

	private get position(): number {
		return window.scrollY;
	}
}
